import React, { useState, useEffect, useCallback } from 'react';
import { Eye, Printer } from 'lucide-react';
import { Modal } from './Helpers';

const DebtorReportView = ({ showToast, currentUser }) => {
  const [debtorData, setDebtorData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedPeriod, setSelectedPeriod] = useState(''); // YYYY-AA format or empty for all periods
  const [selectedResponsiblePartyType, setSelectedResponsiblePartyType] = useState(''); // 'owner', 'resident', or empty for all
  const [breakdownModalOpen, setBreakdownModalOpen] = useState(false);
  const [currentDebtorBreakdown, setCurrentDebtorBreakdown] = useState([]);
  const [selectedDebtorForBreakdown, setSelectedDebtorForBreakdown] = useState(null);

  const loadDebtorData = useCallback(async () => {
    setIsLoading(true);
    try {
      const filters = {};
      if (selectedPeriod) filters.period = selectedPeriod;
      if (selectedResponsiblePartyType) filters.responsiblePartyType = selectedResponsiblePartyType;

      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/debtor-report?` + new URLSearchParams(filters), {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        },
      });
      if (!response.ok) {
        if (response.status === 401 || response.status === 403) {
          localStorage.removeItem('token');
          localStorage.removeItem('currentUser');
          window.location.reload();
          throw new Error('Oturum süresi doldu veya yetkiniz yok.');
        }
        throw new Error('Borçlu raporu verileri yüklenemedi.');
      }
      const data = await response.json();
      setDebtorData(data || []);
    } catch (error) {
      showToast("Borçlu raporu yüklenirken hata: " + error.message, "error");
      setDebtorData([]);
    } finally {
      setIsLoading(false);
    }
  }, [showToast, selectedPeriod, selectedResponsiblePartyType, currentUser]);

  useEffect(() => {
    loadDebtorData();
  }, [loadDebtorData]);

  const openBreakdownModal = async (debtor) => {
    setSelectedDebtorForBreakdown(debtor);
    setIsLoading(true);
    try {
      const url = `${process.env.REACT_APP_API_BASE_URL}/api/debtor-report/${debtor.personId}/${debtor.responsiblePartyType}?${selectedPeriod ? `period=${selectedPeriod}` : ''}`;
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        },
      });
      if (!response.ok) {
        throw new Error('Borç detayları yüklenemedi.');
      }
      const data = await response.json();
      setCurrentDebtorBreakdown(data || []);
      setBreakdownModalOpen(true);
    } catch (error) {
      showToast("Borç detayları yüklenirken hata: " + error.message, "error");
    } finally {
      setIsLoading(false);
    }
  };

  const closeBreakdownModal = () => {
    setBreakdownModalOpen(false);
    setCurrentDebtorBreakdown([]);
    setSelectedDebtorForBreakdown(null);
  };

  const handlePrint = () => {
    const printContent = document.querySelector('.print-area').innerHTML;
    const printWindow = window.open('', '', 'height=600,width=800');
    printWindow.document.write('<html><head><title>Borçlu Raporu</title>');
    const stylesheets = Array.from(document.querySelectorAll('link[rel="stylesheet"]'))
      .map(link => `<link rel="stylesheet" href="${link.href}">`)
      .join('');
    printWindow.document.write(stylesheets);
    printWindow.document.write('<style>');
    printWindow.document.write(`
      body { font-family: sans-serif; margin: 20px; }
      table { width: 100%; border-collapse: collapse; margin-top: 20px; }
      th, td { border: 1px solid #ccc; padding: 8px; text-align: left; }
      .report-header { text-align: center; margin-bottom: 20px; }
      .no-print { display: none !important; }
    `);
    printWindow.document.write('</style></head><body>');
    printWindow.document.write(printContent);
    printWindow.document.write('</body></html>');
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
    printWindow.close();
  };

  return (
    <div className="bg-white p-6 rounded-lg shadow print-area">
      {isLoading && (
        <div className="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center z-30">
          <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-blue-500"></div>
          <p className="ml-3 text-blue-700">Rapor yükleniyor...</p>
        </div>
      )}
      <div className="flex justify-between items-center mb-6 no-print">
        <h1 className="text-2xl font-semibold">Borçlu Raporu</h1>
        <button
          onClick={handlePrint}
          className="bg-green-500 text-white px-4 py-2 rounded-lg hover:bg-green-600 flex items-center disabled:opacity-50"
          disabled={isLoading || debtorData.length === 0}
        >
          <Printer size={18} className="mr-2" /> Yazdır
        </button>
      </div>

      <div className="grid md:grid-cols-2 gap-4 mb-6 p-4 border rounded-lg bg-gray-50 no-print">
        <div>
          <label htmlFor="reportPeriodSelect" className="label-form-style">Dönem Seçin (YYYY-AA):</label>
          <input
            type="month"
            id="reportPeriodSelect"
            value={selectedPeriod}
            onChange={(e) => setSelectedPeriod(e.target.value)}
            className="input-form-style"
          />
          <p className="text-xs text-gray-500 mt-1">Boş bırakılırsa tüm dönemler.</p>
        </div>
        <div>
          <label htmlFor="reportResponsiblePartyType" className="label-form-style">Sorumlu Rolü (Opsiyonel):</label>
          <select
            id="reportResponsiblePartyType"
            value={selectedResponsiblePartyType}
            onChange={(e) => setSelectedResponsiblePartyType(e.target.value)}
            className="input-form-style"
          >
            <option value="">Tüm Roller</option>
            <option value="owner">Kat Maliki</option>
            <option value="resident">Sakin</option>
          </select>
        </div>
      </div>

      {debtorData.length === 0 && !isLoading ? (
        <p className="text-gray-500 text-center py-4">Seçilen filtrelere göre borçlu bulunamadı.</p>
      ) : null}

      {debtorData.length > 0 ? (
        <div className="overflow-x-auto shadow border-b border-gray-200 sm:rounded-lg">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-100">
              <tr>
                <th className="th-style">Borçlu Kişi</th>
                <th className="th-style">Rol</th>
                <th className="th-style">Daire(ler)</th>
                <th className="th-style">Blok(lar)</th>
                <th className="th-style">Toplam Kalan Borç</th>
                <th className="th-style no-print">Detay</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {debtorData.map(debtor => (
                <tr key={`${debtor.personId}-${debtor.responsiblePartyType}`}>
                  <td className="td-style font-medium">{debtor.personName}</td>
                  <td className="td-style">{debtor.responsiblePartyType === 'owner' ? 'Kat Maliki' : 'Sakin'}</td>
                  <td className="td-style">{debtor.apartmentNumbers || '-'}</td>
                  <td className="td-style">{debtor.blockNames || '-'}</td>
                  <td className="td-style font-bold text-red-600">{debtor.totalOutstandingDebt.toFixed(2)} TL</td>
                  <td className="td-style no-print">
                    <button
                      onClick={() => openBreakdownModal(debtor)}
                      className="icon-button-blue"
                      title="Borç Detayları"
                      disabled={isLoading}
                    >
                      <Eye size={18}/>
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}

      {/* Breakdown Modal */}
      {selectedDebtorForBreakdown && (
        <Modal isOpen={breakdownModalOpen} onClose={closeBreakdownModal} title={`${selectedDebtorForBreakdown.personName} - Borç Detayları`} size="xl">
          {isLoading ? (
            <div className="flex justify-center p-4">
              <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
              <p className="ml-3 text-blue-700">Detaylar yükleniyor...</p>
            </div>
          ) : currentDebtorBreakdown.length === 0 ? (
            <p className="text-gray-500">Bu borçlu için detaylı borç dökümü bulunamadı.</p>
          ) : (
            <div className="overflow-x-auto max-h-[60vh]">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50 sticky top-0">
                  <tr>
                    <th className="th-style">Dönem</th>
                    <th className="th-style">Daire</th>
                    <th className="th-style">Blok</th>
                    <th className="th-style">Aidat Kalemi</th>
                    <th className="th-style text-right">Borç Tutarı</th>
                    <th className="th-style text-right">Kalan Tutar</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {currentDebtorBreakdown.map((item, index) => (
                    <tr key={index}>
                      <td className="td-style">{item.Period}</td>
                      <td className="td-style">{item.ApartmentNumber}</td>
                      <td className="td-style">{item.blockName}</td>
                      <td className="td-style">{item.DefinitionName}</td>
                      <td className="td-style text-right">{item.dueAmount.toFixed(2)} TL</td>
                      <td className="td-style text-right text-red-600 font-semibold">{item.outstandingAmountForSummary.toFixed(2)} TL</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <div className="mt-6 flex justify-end">
            <button type="button" onClick={closeBreakdownModal} className="btn-primary-style">Kapat</button>
          </div>
        </Modal>
      )}

      <style jsx>{`
        .label-form-style { display: block; margin-bottom: 0.25rem; font-size: 0.875rem; font-weight: 500; color: #374151; }
        .input-form-style { width: 100%; padding: 0.5rem; border: 1px solid #D1D5DB; border-radius: 0.375rem; box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05); }
        .btn-primary-style { padding: 0.5rem 1rem; font-size: 0.875rem; color: white; background-color: #2563EB; border-radius: 0.375rem; }
        .btn-primary-style:hover { background-color: #1D4ED8; }
        .btn-secondary-style { padding: 0.5rem 1rem; font-size: 0.875rem; color: #374151; background-color: #E5E7EB; border-radius: 0.375rem; }
        .btn-secondary-style:hover { background-color: #D1D5DB; }
        .icon-button-blue { color: #2563eb; } .icon-button-blue:hover { color: #1d4ed8; }
        .th-style { padding: 0.75rem 1.5rem; text-align: left; font-size: 0.75rem; font-weight: 500; color: #6B7280; text-transform: uppercase; letter-spacing: 0.05em; }
        .td-style { padding: 0.75rem 1.5rem; white-space: nowrap; font-size: 0.875rem; color: #374151; }

        @media print {
          .no-print {
            display: none !important;
          }
          .print-area {
            box-shadow: none;
            padding: 0;
          }
          table {
            width: 100%;
            border-collapse: collapse;
          }
          th, td {
            border: 1px solid #ccc;
            padding: 8px;
            font-size: 10px;
            white-space: normal;
          }
          .report-header {
            margin-bottom: 20px;
          }
        }
      `}</style>
    </div>
  );
};

export default DebtorReportView;
